import React, { useState, useEffect } from 'react';
import { useManagerAuth } from '../../context/ManagerAuthContext';
import { 
  Package, Search, AlertTriangle, XCircle, Plus, Minus, X, Check, 
  RefreshCw, Boxes, Lock, CheckCircle2, SlidersHorizontal, ArrowUpRight,
  TrendingUp, ShieldCheck, Warehouse
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4002';

const ManagerInventory = () => {
  const { getToken, manager } = useManagerAuth();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const [adjustModal, setAdjustModal] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [addForm, setAddForm] = useState({ product_id: '', warehouse_id: '', quantity: '' });
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const warehouses = manager?.warehouses || [];

  useEffect(() => { 
    fetchInventory(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterWarehouse, filterStock]);

  const showToast = (msg) => { 
    setToast(msg); 
    setTimeout(() => setToast(''), 3000); 
  };

  const fetchInventory = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filterWarehouse !== 'all') p.set('warehouse_id', filterWarehouse);
    if (filterStock === 'low') p.set('low_stock', 'true');
    if (filterStock === 'out') p.set('out_of_stock', 'true');
    try {
      const r = await fetch(`${API}/api/manager/inventory?${p}`, { 
        headers: { Authorization: `Bearer ${getToken()}` } 
      });
      const d = await r.json();
      if (d.success) setInventory(d.inventory || []);
    } catch (e) { 
      console.error('❌ Error fetching manager inventory:', e); 
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const r = await fetch(`${API}/api/manager/products`, { 
        headers: { Authorization: `Bearer ${getToken()}` } 
      });
      const d = await r.json();
      if (d.success) setProducts(d.products || []);
    } catch (e) { 
      console.error('❌ Error fetching manager products:', e); 
    }
  };

  const handleAdjust = async () => {
    if (!adjustQty || parseInt(adjustQty) === 0) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/manager/inventory/adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ 
          inventory_stock_id: adjustModal.id, 
          warehouse_id: adjustModal.warehouse_id, 
          adjustment: parseInt(adjustQty), 
          reason: adjustReason 
        }),
      });
      const d = await r.json();
      if (d.success) { 
        showToast(d.message); 
        setAdjustModal(null); 
        setAdjustQty(''); 
        setAdjustReason(''); 
        fetchInventory(); 
      } else {
        alert(d.message);
      }
    } catch (e) { 
      alert(e.message); 
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    const warehouseId = warehouses.length === 1 ? warehouses[0].id : addForm.warehouse_id;
    if (!addForm.product_id || !warehouseId || !addForm.quantity) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/manager/inventory/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ 
          product_id: addForm.product_id,
          warehouse_id: warehouseId,
          quantity: parseInt(addForm.quantity) 
        }),
      });
      const d = await r.json();
      if (d.success) { 
        showToast(d.message); 
        setAddModal(false); 
        setAddForm({ product_id: '', warehouse_id: '', quantity: '' }); 
        fetchInventory(); 
      } else {
        alert(d.message);
      }
    } catch (e) { 
      alert(e.message); 
    } finally {
      setSaving(false);
    }
  };

  const filtered = inventory.filter(i => 
    !search || i.product?.name?.toLowerCase().includes(search.toLowerCase())
  );

  // Summary Metrics
  const totalItemsCount = filtered.length;
  const totalOnHandUnits = filtered.reduce((acc, i) => acc + (i.quantity || 0), 0);
  const totalReservedUnits = filtered.reduce((acc, i) => acc + (i.reserved_quantity || 0), 0);
  const totalAvailableUnits = Math.max(0, totalOnHandUnits - totalReservedUnits);
  const criticalStockAlerts = filtered.filter(i => (i.quantity || 0) <= 10).length;

  const StockBadge = ({ qty }) => {
    if (qty === 0) {
      return (
        <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 border border-rose-200 flex items-center gap-1 w-fit shadow-2xs">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-600 animate-ping" />
          OUT OF STOCK
        </span>
      );
    }
    if (qty <= 10) {
      return (
        <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1 w-fit shadow-2xs">
          <AlertTriangle className="w-3 h-3 text-amber-600" />
          LOW STOCK ({qty})
        </span>
      );
    }
    return (
      <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1 w-fit shadow-2xs">
        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
        HEALTHY ({qty})
      </span>
    );
  };

  return (
    <div className="space-y-6 select-none font-sans pb-12 relative">
      
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-5 right-5 z-50 bg-slate-900 text-emerald-400 border border-emerald-500/50 px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-2.5 text-xs font-black uppercase tracking-wider animate-fade-in-down">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span>{toast}</span>
        </div>
      )}

      {/* ── 1. EMERALD TEAL HERO HEADER ── */}
      <div className="relative bg-gradient-to-r from-emerald-950 via-slate-900 to-teal-950 rounded-3xl p-6 sm:p-8 text-white border border-emerald-800/60 shadow-2xl overflow-hidden group">
        
        {/* Ambient Backdrops */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-emerald-500/20 via-teal-500/10 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2.5">
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-xs">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                LIVE INVENTORY AUDIT MODE
              </span>
              <span className="text-xs text-emerald-300 font-mono font-bold">
                ● WAREHOUSE FULFILLMENT READY
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-black tracking-tight uppercase italic text-white flex items-center gap-3">
              STOCK & RESERVES CONTROL ⚡
            </h1>

            <p className="text-xs sm:text-sm text-slate-300 font-medium max-w-xl">
              Audit on-hand stock quantities, reserved order allocations, net available reserves, and perform stock adjustments.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={fetchInventory}
              disabled={loading}
              className="p-3 bg-slate-800/80 hover:bg-slate-700 border border-slate-600 rounded-2xl text-slate-300 hover:text-white transition-all shadow-md active:scale-95 disabled:opacity-50"
              title="Refresh Inventory"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-emerald-400' : ''}`} />
            </button>

            <button
              onClick={() => { setAddModal(true); fetchProducts(); }}
              className="px-5 py-3 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-500 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-black uppercase tracking-wider rounded-2xl flex items-center gap-2 shadow-lg shadow-emerald-600/30 transition-all hover:scale-105 active:scale-95"
            >
              <Plus className="w-4 h-4" />
              <span>Add Stock Node</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── 2. INVENTORY TELEMETRY METRICS ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Card 1: On-Hand Stock */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600">
              <Boxes className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-900 font-mono">{totalOnHandUnits.toLocaleString()}</p>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">On-Hand Quantity</p>
            </div>
          </div>
          <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
            PHYSICAL
          </span>
        </div>

        {/* Card 2: Reserved Stock */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-900 font-mono">{totalReservedUnits.toLocaleString()}</p>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Reserved for Orders</p>
            </div>
          </div>
          <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200">
            LOCKED
          </span>
        </div>

        {/* Card 3: Net Available */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-600">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-900 font-mono">{totalAvailableUnits.toLocaleString()}</p>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Net Available</p>
            </div>
          </div>
          <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 border border-teal-200">
            DISPATCHABLE
          </span>
        </div>

        {/* Card 4: Critical Alerts */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${
              criticalStockAlerts > 0 
                ? 'bg-amber-50 border-amber-200 text-amber-600' 
                : 'bg-slate-50 border-slate-200 text-slate-400'
            }`}>
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-900 font-mono">{criticalStockAlerts}</p>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Restock Alerts</p>
            </div>
          </div>
          <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${
            criticalStockAlerts > 0
              ? 'bg-amber-100 text-amber-800 border-amber-300'
              : 'bg-slate-100 text-slate-600 border-slate-200'
          }`}>
            {criticalStockAlerts > 0 ? 'ATTENTION' : 'CLEAR'}
          </span>
        </div>

      </div>

      {/* ── 3. SEARCH & CONTROLS TOOLBAR ── */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        
        {/* Search Field */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search inventory items by product name..."
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-900 font-medium placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
          />
        </div>

        {/* Filter Controls */}
        <div className="flex flex-wrap items-center gap-2">
          
          {warehouses.length > 1 && (
            <select
              value={filterWarehouse}
              onChange={(e) => setFilterWarehouse(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:border-emerald-500 transition-colors"
            >
              <option value="all">All Warehouses</option>
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>{w.code} – {w.name}</option>
              ))}
            </select>
          )}

          <select
            value={filterStock}
            onChange={(e) => setFilterStock(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:border-emerald-500 transition-colors"
          >
            <option value="all">All Stock Status</option>
            <option value="low">Low Stock (≤10)</option>
            <option value="out">Out of Stock (0)</option>
          </select>

          <span className="text-xs font-black text-slate-400 uppercase tracking-widest ml-auto">
            {totalItemsCount} RECORD(S)
          </span>
        </div>
      </div>

      {/* ── 4. OPERATIONS TABLE ── */}
      <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Auditing Stock Telemetry...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3 text-center">
            <Package className="w-14 h-14 text-slate-300" />
            <h3 className="text-sm font-black uppercase text-slate-800">No Inventory Records Found</h3>
            <p className="text-xs font-medium text-slate-400 max-w-sm">No items match your selected warehouse or stock status filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-wider text-slate-500">
                  <th className="p-4">PRODUCT &amp; CATEGORY</th>
                  <th className="p-4">ON HAND QTY</th>
                  <th className="p-4">RESERVED</th>
                  <th className="p-4">NET AVAILABLE</th>
                  <th className="p-4">AVAILABILITY RATIO</th>
                  <th className="p-4">STATUS</th>
                  <th className="p-4 text-right">ACTION</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium">
                {filtered.map(item => {
                  const onHand = item.quantity || 0;
                  const reserved = item.reserved_quantity || 0;
                  const netAvail = Math.max(0, onHand - reserved);
                  const availPct = onHand > 0 ? Math.min(100, Math.round((netAvail / onHand) * 100)) : 0;

                  return (
                    <tr key={item.id} className="hover:bg-emerald-50/30 transition-colors group">
                      
                      {/* Product Column */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-2xl bg-slate-50 border border-slate-200 p-1 flex items-center justify-center shrink-0 overflow-hidden group-hover:border-emerald-300 transition-colors">
                            {item.product?.image_url ? (
                              <img src={item.product.image_url} alt="" className="w-full h-full object-contain" />
                            ) : (
                              <Package className="w-5 h-5 text-slate-300" />
                            )}
                          </div>
                          <div>
                            <p className="font-black text-slate-900 uppercase text-xs line-clamp-1 group-hover:text-emerald-700 transition-colors">
                              {item.product?.name || 'Unlisted SKU'}
                            </p>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                              {item.product?.category || 'GENERAL'}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* On Hand Qty */}
                      <td className="p-4 font-mono font-black text-slate-900 text-sm">
                        {onHand.toLocaleString()}
                      </td>

                      {/* Reserved */}
                      <td className="p-4 font-mono text-slate-500 font-bold">
                        {reserved.toLocaleString()}
                      </td>

                      {/* Net Available */}
                      <td className="p-4 font-mono font-black text-emerald-700 text-sm">
                        {netAvail.toLocaleString()}
                      </td>

                      {/* Availability Ratio Progress */}
                      <td className="p-4 w-44">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-[10px] font-mono font-bold">
                            <span className="text-slate-500">{availPct}%</span>
                            <span className="text-slate-400">{netAvail}/{onHand}</span>
                          </div>
                          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ${
                                availPct > 60 ? 'bg-gradient-to-r from-emerald-400 to-green-500' :
                                availPct > 25 ? 'bg-gradient-to-r from-amber-400 to-orange-500' :
                                               'bg-gradient-to-r from-rose-500 to-red-600'
                              }`}
                              style={{ width: `${availPct}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="p-4">
                        <StockBadge qty={onHand} />
                      </td>

                      {/* Action Button */}
                      <td className="p-4 text-right">
                        <button
                          onClick={() => setAdjustModal({
                            id: item.id,
                            warehouse_id: item.warehouse_id,
                            current_qty: onHand,
                            product_name: item.product?.name
                          })}
                          className="px-3.5 py-1.5 bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white border border-emerald-200 hover:border-emerald-600 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all shadow-2xs hover:scale-105 active:scale-95"
                        >
                          Adjust
                        </button>
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── 5. GLASSMORPHISM ADJUST STOCK MODAL ── */}
      {adjustModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-[2rem] shadow-2xl max-w-md w-full p-6 sm:p-8 border border-slate-200 relative overflow-hidden space-y-5">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-700">
                  <SlidersHorizontal className="w-5 h-5" />
                </div>
                <h3 className="text-base font-black text-slate-900 uppercase tracking-tight">Adjust Stock Quantity</h3>
              </div>
              <button
                onClick={() => { setAdjustModal(null); setAdjustQty(''); setAdjustReason(''); }}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Target Item Specs */}
            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 space-y-1">
              <p className="text-xs font-black text-slate-900 uppercase">{adjustModal.product_name}</p>
              <p className="text-[11px] text-slate-500 font-mono">
                Current On-Hand Stock: <strong className="text-slate-900">{adjustModal.current_qty} units</strong>
              </p>
            </div>

            {/* Adjustment Stepper Input */}
            <div className="space-y-2">
              <label className="block text-xs font-black text-slate-700 uppercase tracking-wider">
                Adjustment Delta (+ / -)
              </label>
              <p className="text-[11px] text-slate-400 font-medium">Use positive numbers to add stock, negative to subtract stock.</p>
              
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => setAdjustQty(v => String((parseInt(v) || 0) - 1))}
                  className="w-12 h-12 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-lg transition-colors border border-slate-200"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <input
                  type="number"
                  value={adjustQty}
                  onChange={(e) => setAdjustQty(e.target.value)}
                  placeholder="0"
                  className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl py-3 text-center text-2xl font-black font-mono text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                />
                <button
                  onClick={() => setAdjustQty(v => String((parseInt(v) || 0) + 1))}
                  className="w-12 h-12 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-lg transition-colors border border-slate-200"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              {adjustQty && parseInt(adjustQty) !== 0 && (
                <div className="text-center pt-2">
                  <span className="text-xs font-black text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full font-mono">
                    New On-Hand Total: {adjustModal.current_qty + parseInt(adjustQty)} units
                  </span>
                </div>
              )}
            </div>

            {/* Audit Reason Textarea */}
            <div className="space-y-1.5">
              <label className="block text-xs font-black text-slate-700 uppercase tracking-wider">
                Audit Reason <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <textarea
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                rows={2}
                placeholder="e.g. Received shipment batch #104, damaged goods discarded..."
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-3 text-xs text-slate-900 font-medium placeholder-slate-400 outline-none focus:border-emerald-500 transition-colors"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => { setAdjustModal(null); setAdjustQty(''); setAdjustReason(''); }}
                className="flex-1 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-100 uppercase tracking-wider transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAdjust}
                disabled={saving || !adjustQty || parseInt(adjustQty) === 0}
                className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-2xl text-xs font-black uppercase tracking-wider shadow-lg shadow-emerald-600/20 disabled:opacity-50 transition-all hover:scale-105"
              >
                {saving ? 'Confirming...' : 'Confirm Adjustment'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── 6. ADD INITIAL STOCK MODAL ── */}
      {addModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-[2rem] shadow-2xl max-w-md w-full p-6 sm:p-8 border border-slate-200 relative overflow-hidden space-y-5">
            
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-700">
                  <Plus className="w-5 h-5" />
                </div>
                <h3 className="text-base font-black text-slate-900 uppercase tracking-tight">Add Stock to Hub</h3>
              </div>
              <button
                onClick={() => setAddModal(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-black text-slate-700 uppercase tracking-wider">Select Product *</label>
                <select
                  value={addForm.product_id}
                  onChange={(e) => setAddForm(f => ({ ...f, product_id: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-3 text-xs font-bold text-slate-900 outline-none focus:border-emerald-500 transition-colors"
                >
                  <option value="">Select a product SKU...</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.category})</option>
                  ))}
                </select>
              </div>

              {warehouses.length > 1 && (
                <div className="space-y-1.5">
                  <label className="block text-xs font-black text-slate-700 uppercase tracking-wider">Fulfillment Warehouse *</label>
                  <select
                    value={addForm.warehouse_id}
                    onChange={(e) => setAddForm(f => ({ ...f, warehouse_id: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-3 text-xs font-bold text-slate-900 outline-none focus:border-emerald-500 transition-colors"
                  >
                    <option value="">Select target warehouse...</option>
                    {warehouses.map(w => (
                      <option key={w.id} value={w.id}>{w.code} – {w.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="block text-xs font-black text-slate-700 uppercase tracking-wider">Initial Stock Quantity *</label>
                <input
                  type="number"
                  min="1"
                  value={addForm.quantity}
                  onChange={(e) => setAddForm(f => ({ ...f, quantity: e.target.value }))}
                  placeholder="e.g. 50"
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-3 text-xs font-bold text-slate-900 outline-none focus:border-emerald-500 font-mono"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setAddModal(false)}
                className="flex-1 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-100 uppercase tracking-wider transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={saving || !addForm.product_id || !addForm.quantity}
                className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-2xl text-xs font-black uppercase tracking-wider shadow-lg shadow-emerald-600/20 disabled:opacity-50 transition-all hover:scale-105"
              >
                {saving ? 'Adding...' : 'Add Stock Node'}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default ManagerInventory;

